import React from 'react';

// The desktop table and the mobile card list render the same badge and the same
// button for each payment, so the two status maps live here rather than being
// spelled out twice.
const STATUS_BADGE = {
  'Payment Withdrawn': 'bg-yellow-100 text-yellow-800',
  'Payment Avail': 'bg-green-100 text-green-800',
  'New Dots User': 'bg-blue-100 text-blue-800',
};

const STATUS_BUTTON = {
  'Payment Withdrawn': 'bg-primary hover:bg-primary-dark',
  'Payment Avail': 'bg-green-600 hover:bg-green-700',
  'New Dots User': 'bg-blue-600 hover:bg-blue-700',
};

const STATUS_ACTION = {
  'Payment Withdrawn': 'View Status',
  'Payment Avail': 'Claim',
  'New Dots User': 'Sign Up',
};

const badgeClass = (status) => STATUS_BADGE[status] || 'bg-gray-100 text-gray-800';
const buttonClass = (status) => STATUS_BUTTON[status] || 'bg-gray-600 hover:bg-gray-700';
const actionLabel = (status) => STATUS_ACTION[status] || 'View';

const PaymentsSection = ({ djElection }) => {
  const payments = djElection?.payments || [];

  return (
    <div id="payments-section" className="bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold text-gray-900">
          Payments
        </h3>
        <span className="px-3 py-1 text-sm font-medium text-gray-800 bg-gray-100 rounded-lg">
          {payments.length > 0 ? `${payments.length} Payment(s)` : 'No Payments Yet'}
        </span>
      </div>
      <div className="space-y-6">
        {payments.length > 0 ? (
          <div className="overflow-hidden">
            {/* Payment processing note */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    After claiming your payment, it may take 3-5 business days for the funds to be deposited in your account. If you don't see your payment within that time, check the status at <a href="https://my.dots.dev" className="underline hover:text-blue-900">my.dots.dev</a>, or check with the payment method you selected. (ie, CashApp, Venmo, etc.)
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop view - Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment, index) => (
                    <tr key={payment.rid || index}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClass(payment.payment_status)}`}>
                          {payment.payment_status.charAt(0).toUpperCase() + payment.payment_status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payment.amount}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{payment.payment_type}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {payment.payment_link && (
                          <a
                            href={payment.payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${buttonClass(payment.payment_status)} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors`}
                          >
                            {actionLabel(payment.payment_status)}
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile view - Card layout */}
            <div className="md:hidden space-y-4">
              {payments.map((payment, index) => (
                <div key={payment.rid || index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClass(payment.payment_status)}`}>
                      {payment.payment_status.charAt(0).toUpperCase() + payment.payment_status.slice(1)}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{payment.amount}</span>
                  </div>
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Payment Type</p>
                    <p className="text-sm text-gray-700">{payment.payment_type}</p>
                  </div>
                  {payment.payment_link && (
                    <div className="mt-3">
                      <a
                        href={payment.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${buttonClass(payment.payment_status)} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors`}
                      >
                        {actionLabel(payment.payment_status)}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="col-span-full">
                <p className="text-sm text-gray-600">
                  After you have completed your set, you can access your payout links here
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center text-sm text-gray-600">
                  <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Complete your first set to view payment details
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                disabled
                className="inline-flex items-center px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-400 bg-gray-50 cursor-not-allowed"
                title="Available after completing sets"
              >
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View Payments
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentsSection;
